import React, { useState, useMemo, useEffect, Suspense } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Menu, MenuItem, Divider } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useSelector } from "react-redux";
import { RootState } from "@root/store/store";
import { ScreenPaths } from "@root/configs/paths";
import { DashboardSidebar, NAV_ITEMS } from "./DashboardSidebar";
import logout from "../layouts/img/SignOut.svg";
import logo_cashd from "@root/assets/LoginV2/logo_cashd.svg";
import { CircularProgress } from "@mui/material";
import T from "@root/T";
import { LogoutDialog } from "@root/components/dialog/Logout";

const DashboardLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { primaryColor, logoUrl, custom } = useSelector(
    (state: RootState | any) => state.whiteLabellingData
  );

  const navigate = useNavigate();

  const location = useLocation();


  const user: any = useSelector((state: RootState) => state.userInfo.value);
  const userInfor: any = user?.result?.data;

  const headerTitle = useMemo(() => {
    switch (location.pathname) {
      case ScreenPaths.TRANSACTIONS_PATH:
        return T.TRANSACTIONS;
      case ScreenPaths.PROFILE_PATH:
        return T.PROFILE;
      case ScreenPaths.WITHDRAWAL_PATH:
        return T.WITHDRAW_MONEY;
      case ScreenPaths.PAYMENT_SETTINGS:
        return T.PAYMENT_SETTINGS;
      case ScreenPaths.DEDUCTION_PATH:
        return T.SUMMARY;
      case ScreenPaths.SUPPORT_PATH:
        return T.SUPPORT;
      case ScreenPaths.RECURRING_WITHDRAWAL_NEW:
        return T.SETUP_RECURRING_PAYMENT;
      default:
        return `Hello, ${userInfor?.first_name || "there"}`;
    }
  }, [location.pathname, userInfor?.first_name]);

  const handleToggleMobile = () => setMobileOpen((prev) => !prev);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) =>
    setUserMenuAnchor(event.currentTarget);

  const handleCloseUserMenu = () => setUserMenuAnchor(null);

  const handleGoProfile = () => {
    navigate(ScreenPaths.PROFILE_PATH);
    handleCloseUserMenu();
    setMobileOpen(false);
  };

  const handleGoPaymentMethods = () => {
    navigate(ScreenPaths.PAYMENT_SETTINGS);
    handleCloseUserMenu();
    setMobileOpen(false);
  };

  const handleRecurringWithdrawalMethods = () => {
    navigate(ScreenPaths.RECURRING_WITHDRAWAL_NEW);
    handleCloseUserMenu();
    setMobileOpen(false);
  };

  const handleLogoutClick = () => {
    setIsLogoutDialogOpen(true);
    handleCloseUserMenu();
    setMobileOpen(false);
  };
  const handleCloseLogoutDialog = (value: string) => {
    if (value === 'Yes') {
      localStorage.clear();
      navigate(ScreenPaths.LOGIN_PATH);
    }
    setIsLogoutDialogOpen(false);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#fff]">
      {/* Sidebar - Desktop Only */}
      <div className="hidden lg:block h-full">
        <DashboardSidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
        />
      </div>

      {/* Main Section */}
      <div className="flex flex-col flex-1 pt-[0px] bg-white overflow-hidden" >
        {/* Top Bar */}

        <div className="h-[64px] pr-3 pl-2 md:pl-2 lg:pl-8 lg:pr-10 flex items-stretch items-center lg:bg-white relative"
          style={{
            backgroundColor: isMobile && primaryColor ? primaryColor : 'white',
            color: isMobile && primaryColor ? 'white' : 'black',
          }} >
          <div className="flex items-center justify-between w-full h-auto lg:h-full lg:border-b lg:border-[#ccc]">
            <div className="flex items-center items-center gap-[10px]">
              {/* Hamburger (Opens Drawer on Mobile, Hidden on Desktop) */}
              <button
                onClick={() => setMobileOpen(true)}
                className="p-2 lg:hidden"
              >
                <MenuIcon sx={{ color: isMobile && primaryColor ? 'white' : 'black' }} />
              </button>
              <h2 className="text-xl font-semibold">
                {headerTitle}
              </h2>
            </div>

            <div className="flex items-center gap-4">
              {/* User Dropdown (Desktop Only) */}
              <div
                onClick={handleOpenUserMenu}
                className="hidden lg:flex items-center cursor-pointer"
              >
                {userInfor?.avatar_path ? (
                  <img
                    src={userInfor.avatar_path}
                    alt="avatar"
                    className="w-8 h-8 mr-2 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <AccountCircleIcon className="mr-2" style={{ fontSize: 32, color: primaryColor }}  />
                )}
                <span className="text-base font-medium"
                  style={{ color: "black" }}
                >
                  {userInfor?.fullname}
                </span>
                <ArrowDropDownIcon />
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div id="dashboard-content" className="flex flex-col flex-1 !p-3 !py-6 lg:!p-8 overflow-y-auto bg-white min-h-0">
          <Suspense fallback={
            <div className="flex-1 flex items-center justify-center min-h-[400px]">
              <CircularProgress sx={{ color: primaryColor || "#2E5090" }} />
            </div>
          }>
            <Outlet />
          </Suspense>
        </div>
      </div>

      {/* Mobile / Tablet Drawer */}
      {
        mobileOpen && (
          <div
            className="fixed inset-0 z-[100] bg-black/50 lg:hidden"
            onClick={() => setMobileOpen(false)}
          >
            <div
              className="absolute left-0 top-0 h-full w-[300px] shadow-2xl flex flex-col"
              style={{ backgroundColor: primaryColor || "#2E5090" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Sidebar Header */}
              <div className="relative flex items-start justify-start px-4 pt-8 pb-4">
                {custom ? (
                  <>
                    {logoUrl ? (
                      <img
                        className="img-fluid cursor-pointer  max-w-[172px]"
                        src={logoUrl}
                        alt="logo"
                        onClick={() => navigate("/")}
                      />
                    ) : (
                      <img
                        className="img-fluid cursor-pointer  max-w-[172px]"
                        src={logo_cashd}
                        alt="logo"
                        onClick={() => navigate("/")}
                      />
                    )}
                  </>
                ) : (
                  <img
                    className="img-fluid cursor-pointer  max-w-[172px]"
                    src={logo_cashd}
                    alt="logo"
                    onClick={() => navigate("/")}
                  />
                )}
                <button className="absolute top-[38%] right-5 text-white" onClick={() => setMobileOpen(false)}>
                  <CloseIcon sx={{ fontSize: 28 }} />
                </button>
              </div>
              <div
                className="border-t border-white/30 mx-4"
              />

              {/* Navigation Section */}
              <div className="flex flex-col flex-1 px-4 overflow-y-auto pb-6">
                {/* Thin Divider under logo */}
                <div className="h-[0.5px] bg-white/30 mx-1 mb-6" />

                {/* Primary Nav Items */}
                <div className="flex flex-col gap-1.5 mb-8">
                  {NAV_ITEMS.map(({ label, path, Icon, strokeOnly }) => {
                    const active = location.pathname === path || location.pathname.startsWith(path + "/");
                    const iconClassName = strokeOnly
                      ? "[&_path]:stroke-current [&_path]:fill-none"
                      : "[&_path]:fill-current [&_path]:stroke-none";

                    return (
                      <NavLink
                        key={label}
                        to={path}
                        onClick={handleToggleMobile}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                          ${active ? "bg-white text-[#2E5090]" : "text-white hover:bg-white/5"}`}
                        style={{ color: active ? (primaryColor || "#2E5090") : "white" }}
                      >
                        <Icon
                          width={20} height={20}
                          className={iconClassName}
                        />
                        <span className="text-[16px] font-medium">{label}</span>
                      </NavLink>
                    );
                  })}
                </div>

                {/* Spacer to push secondary items down if needed, 
                    but in the image they just follow the primary list at a distance */}
                <div className="mt-auto flex flex-col gap-1">
                  {/* Secondary Items (Profile, Payment Details, etc.) */}
                  {[
                    { label: "Profile", path: ScreenPaths.PROFILE_PATH },
                    { label: "Payment Details", path: ScreenPaths.PAYMENT_SETTINGS },
                    { label: "Recurring Payments", path: ScreenPaths.RECURRING_WITHDRAWAL_NEW },
                  ].map(({ label, path }) => {
                    const active = location.pathname === path;
                    return (
                      <NavLink
                        key={label}
                        to={path}
                        onClick={handleToggleMobile}
                        className={`px-4 py-2.5 text-[16px] font-normal transition-all rounded-md
                          ${active ? "bg-white" : "text-white hover:bg-white/5"}`}
                      >
                        {label}
                      </NavLink>
                    );
                  })}

                  {/* Divider above Logout */}
                  <div className="h-[0.5px] bg-white/30 mx-1 my-3" />

                  {/* Logout Button */}
                  <button
                    onClick={handleLogoutClick}
                    className="flex items-center gap-3 px-4 py-3 text-white font-medium hover:bg-white/5 rounded-md transition-all"
                  >
                    <img src={logout} alt="Logout" className="w-5 h-5"
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                    <span className="text-[15px]">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* User Dropdown (Desktop) */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={handleGoProfile}
          sx={{
            fontWeight: 400,
            fontSize: "16px",
            color: "#5C5C5C",
          }}
        >
          Profile
        </MenuItem>
        <MenuItem
          onClick={handleGoPaymentMethods}
          sx={{
            fontWeight: 400,
            fontSize: "16px",
            color: "#5C5C5C",
          }}
        >
          Payment Details
        </MenuItem>
        <MenuItem
          onClick={handleRecurringWithdrawalMethods}
          sx={{
            fontWeight: 400,
            fontSize: "16px",
            color: "#5C5C5C",
          }}
        >
          Recurring Withdrawals
        </MenuItem>
        <Divider
          sx={{
            width: "80%",
            mx: "auto",
            my: 1,
            bgcolor: "#E2E8F0",
          }}
        />
        <MenuItem
          onClick={handleLogoutClick}
          sx={{
            color: "error.main",
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 400,
            fontSize: 16,
          }}
        >
          Logout
          <img src={logout} alt="Logout" width={18} />
        </MenuItem>
      </Menu>
      <LogoutDialog open={isLogoutDialogOpen} onClose={handleCloseLogoutDialog} />
    </div >
  );
};

export default DashboardLayout;
